import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import _ from 'lodash';

import {
  Box, Text, Button, Meter,
} from 'grommet';
import { FormClose } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import { AppButton, SimpleModal } from '@Components/Control';
import { baseServerUrl } from '@Helpers/util';
import { ASSET_UPLOAD_CHUNK_SIZE } from '@Components/Partial/Product/ContentId/constants';
import { AddButton } from './ProductFormComponent';
import { StyledUploadContainer, StyledUploadInput } from './StyledProductFormComponent';


const extractFiles = (data) => data.filter((dt) => dt.added).map((dt) => dt.file);
const renderNewFile = () => ({ id: uniqid(), file: null, added: false });

const MediaUpload = ({
  small,
  singleFile = false,
  maxInputs = 1,
  handleFormValues,
  searchTypeKey = null,
  inputConfig = null,
  isBulkSearch = false,
  useCatalog = false,
  onUseCatalogChange = null,
  companyId = null,
  onUploadStart = null,
  onUploadEnd = null,
  ...rest
}) => {
  const [inputs, setInputs] = React.useState([renderNewFile()]);
  const [showModal, setShowModal] = React.useState(false);
  const { theme: { page, button, navigation } } = useAppTheme();
  const [s3Urls, setS3Urls] = React.useState({});
  const [uploadingIds, setUploadingIds] = React.useState(new Set());
  const [uploadProgress, setUploadProgress] = React.useState({});
  const [uploadError, setUploadError] = React.useState(null);
  const s3UrlsRef = React.useRef({});

  React.useEffect(() => {
    s3UrlsRef.current = s3Urls;
  }, [s3Urls]);

  const useS3Upload = !isBulkSearch && !!companyId && !!onUploadStart && !!onUploadEnd;

  const uploadFileToS3 = async (file, onProgress) => {
    const initRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'init',
        fileName: file.name,
        contentType: file.type,
        requestId: 'searches',
        companyId,
      }),
    });
    if (!initRes.ok) throw new Error('Upload initialization failed');
    const { uploadId, key } = await initRes.json();

    const partCount = Math.ceil(file.size / ASSET_UPLOAD_CHUNK_SIZE);
    const etags = [];

    for (let idx = 0; idx < partCount; idx += 1) {
      const partNumber = idx + 1;
      const start = idx * ASSET_UPLOAD_CHUNK_SIZE;
      const end = Math.min(start + ASSET_UPLOAD_CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);

      /* eslint-disable no-await-in-loop */
      const urlRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'part',
          fileName: file.name,
          requestId: 'searches',
          uploadId,
          partNumber,
          companyId,
        }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { url } = await urlRes.json();

      const uploadRes = await fetch(url, { method: 'PUT', body: blob });
      if (!uploadRes.ok) throw new Error('Upload part failed');
      const etag = uploadRes.headers.get('etag') || uploadRes.headers.get('ETag');
      etags.push({ ETag: etag.replace(/"/g, ''), PartNumber: partNumber });
      if (onProgress) onProgress(Math.round((partNumber / partCount) * 100));
      /* eslint-enable no-await-in-loop */
    }

    const completeRes = await fetch(`${baseServerUrl()}api/s3Multipart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'complete',
        fileName: file.name,
        requestId: 'searches',
        uploadId,
        parts: etags,
        companyId,
      }),
    });
    if (!completeRes.ok) throw new Error('Upload completion failed');
    // Return the S3 key captured from the init response
    // (complete returns { status: "completed" }, not the URL/key)
    return key;
  };

  const addInput = async () => {
    const prevInputs = [...inputs];
    const lastInput = prevInputs[prevInputs.length - 1];

    if (!lastInput.file) return;

    const { id, file } = lastInput;
    const addInputs = [...prevInputs];
    addInputs[addInputs.length - 1].added = true;

    if (!maxInputs || addInputs.length < maxInputs) {
      addInputs.push(renderNewFile());
    }

    setInputs(addInputs);

    if (useS3Upload) {
      setUploadingIds((prev) => new Set([...prev, id]));
      setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
      setUploadError(null);
      onUploadStart();
      try {
        const url = await uploadFileToS3(file, (pct) => {
          setUploadProgress((prev) => ({ ...prev, [id]: pct }));
        });
        s3UrlsRef.current = { ...s3UrlsRef.current, [id]: url };
        setS3Urls({ ...s3UrlsRef.current });
        const urlValues = addInputs
          .filter((inp) => inp.added)
          .map((inp) => s3UrlsRef.current[inp.id])
          .filter(Boolean);
        handleFormValues(singleFile ? urlValues[0] : urlValues);
      } catch (err) {
        setUploadError('Upload failed. Please remove the file and try again.');
      } finally {
        setUploadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        onUploadEnd();
      }
    } else {
      const fileValues = extractFiles(addInputs);
      const useValue = singleFile ? fileValues[0] : fileValues;
      handleFormValues(useValue);
    }
  };

  const removeInput = (index) => {
    const prevInputs = [...inputs];
    const removedInput = prevInputs[index];
    const remInputs = prevInputs.filter((__, i) => i !== index);
    const { length } = remInputs;

    if (remInputs[length - 1]?.added || length === 0) {
      remInputs.push(renderNewFile());
    }

    setInputs(remInputs);

    if (useS3Upload) {
      if (removedInput) {
        if (s3UrlsRef.current[removedInput.id]) {
          const newUrls = { ...s3UrlsRef.current };
          delete newUrls[removedInput.id];
          s3UrlsRef.current = newUrls;
          setS3Urls(newUrls);
        }
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[removedInput.id];
          return next;
        });
      }
      const urlValues = remInputs
        .filter((inp) => inp.added)
        .map((inp) => s3UrlsRef.current[inp.id])
        .filter(Boolean);
      handleFormValues(singleFile ? null : urlValues);
    } else {
      const fileValues = extractFiles(remInputs);
      const useValue = singleFile ? null : fileValues;
      handleFormValues(useValue);
    }
  };

  const handleFile = (index, e) => {
    const fileInputs = [...inputs];
    [fileInputs[index].file] = e.currentTarget.files;

    setInputs(fileInputs);
    if (singleFile && fileInputs[index].file) {
      // Automatically add the file and mark as added
      fileInputs[index].added = true;
      setInputs(fileInputs);
      handleFormValues(fileInputs[index].file);
    }
  };

  let acceptedFileTypes = 'image/*, video/*';
  let acceptedFileTypeDisplay = 'image/video';

  if (useS3Upload) {
    acceptedFileTypes = Array.isArray(inputConfig?.acceptedFileType)
    && inputConfig?.acceptedFileType.length
      ? inputConfig.acceptedFileType.join(', ')
      : 'image/*, video/*';
    acceptedFileTypeDisplay = acceptedFileTypes.replaceAll('/*', '').replaceAll(', ', '/');
  } else if (inputConfig && inputConfig.acceptedFileType) {
    acceptedFileTypes = inputConfig.acceptedFileType.join(',');
    acceptedFileTypeDisplay = inputConfig.acceptedFileType.join('/').replaceAll('/*', '');
  }

  let customHint = null;
  let isArrayHint = false;
  let requiredHeaders = null;
  let optionalHeaders = null;
  const useFileHint = Boolean(
    inputConfig.config?.format[searchTypeKey] || Array.isArray(inputConfig.config?.format),
  );

  if (inputConfig?.config?.format && useFileHint) {
    customHint = inputConfig.config.format[searchTypeKey]?.description
      || inputConfig.config.format[searchTypeKey]
      || inputConfig.config.format;

    if (Array.isArray(customHint)) {
      isArrayHint = true;
      [requiredHeaders, optionalHeaders] = _.partition(customHint, 'required');
    }
  }

  const handleDownloadExampleCSV = () => {
    if (!isArrayHint || !customHint) return;

    const columnNames = customHint.map(({ name }) => name);
    const csvContent = `${columnNames.join(',')}\n`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_search_creation_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const hasUploadedFile = inputs.some((input) => input.file && input.added);
  const isUploading = uploadingIds.size > 0;

  const handleCatalogClick = () => {
    if (onUseCatalogChange) {
      onUseCatalogChange(!useCatalog);
    }
  };

  return (
    <Box gap="small" {...rest}>
      {isBulkSearch && onUseCatalogChange && (
        <Box
          direction="row"
          gap="0.5rem"
          align="center"
          pad={{ bottom: '0.5rem' }}
          border={{ side: 'bottom', color: navigation.border }}
        >
          <AppButton
            overrideHover
            level="dynamicSmall"
            color={button.primaryText}
            width="8rem"
            bgColor={button.primaryBg}
            label={useCatalog ? 'Using catalog' : 'Use catalog'}
            onClick={handleCatalogClick}
            fontWeight={600}
            disabled={hasUploadedFile}
          />
          {useCatalog && (
            <Text size="0.75rem" color={page.primaryText}>
              Searches will be created from your catalog of titles
            </Text>
          )}
          {!hasUploadedFile && !useCatalog && (
            <Text size="0.75rem" color={page.primaryText}>
              Use your catalog of titles to create searches instead of a CSV file
            </Text>
          )}
          {hasUploadedFile && !useCatalog && (
            <Text size="0.75rem" color={page.primaryText}>
              Remove file to use catalog
            </Text>
          )}
        </Box>
      )}
      {customHint && (
        <Button plain onClick={() => setShowModal(true)}>
          <Text size="0.875rem" color={page.altHighlight}>
            View CSV File Format
          </Text>
        </Button>
      )}
      {uploadError && (
        <Text size="0.875rem" color="var(--app-error)">{uploadError}</Text>
      )}
      {inputs.map(({ id, file, added }, i) => {
        const isFileUploading = uploadingIds.has(id);
        const isFileUploaded = useS3Upload && !!s3UrlsRef.current[id];

        return (
          <Box key={id} gap="small">
            <Box direction="row" gap="1rem" width={small ? '100%' : '90%'}>
              <StyledUploadContainer highlight={page.focusHighlight}>
                <Box fill gap="1rem" align="center" direction="row">
                  <Text truncate size="0.875rem" textAlign="end" className={!file ? 'preline' : null}>
                    {file ? file.name : `No ${acceptedFileTypeDisplay} file selected`}
                  </Text>
                  {isFileUploaded && !isFileUploading && (
                    <Text size="0.75rem" color={page.statusSuccess}>Uploaded</Text>
                  )}
                  <AppButton
                    level="xsmall"
                    disabled={added || useCatalog}
                    label="Choose File..."
                    color={page.focusHighlight}
                  />
                </Box>
                <StyledUploadInput
                  disabled={added || useCatalog}
                  type="file"
                  accept={acceptedFileTypes}
                  onChange={(e) => handleFile(i, e)}
                  highlight={page.focusHighlight}
                />
              </StyledUploadContainer>
              {added && !isFileUploading && (
                <Button
                  plain
                  icon={<FormClose size="1.25rem" color={button.iconColor} />}
                  onClick={() => removeInput(i)}
                />
              )}
            </Box>
            {isFileUploading && (
              <Box direction="row" align="center" gap="0.5rem" pad={{ top: '0.25rem' }}>
                <Meter
                  values={[{ value: uploadProgress[id] ?? 0, color: page.highlight }]}
                  thickness="small"
                  size="small"
                  background="light-3"
                  max={100}
                />
                <Text size="0.8rem" color={page.tableText}>
                  {`${uploadProgress[id] ?? 0}%`}
                </Text>
              </Box>
            )}
            {!added && !singleFile && (
              <AddButton
                disabled={!file || useCatalog || isUploading}
                onClick={() => addInput()}
                highlight={page.focusHighlight}
              />
            )}
          </Box>
        );
      })}
      {showModal && (
        <SimpleModal
          hideLowerSection
          small={small}
          toggleModal={setShowModal}
          modalTitle="CSV File Format"
          highlightColor={page.highlight}
        >
          {customHint && !isArrayHint && (
            <Box gap="small" width="100%">
              <Text size="1.2rem" color={page.modalText} style={{ whiteSpace: 'pre-line', lineHeight: '1.4' }}>
                {customHint}
              </Text>
            </Box>
          )}
          {isArrayHint && requiredHeaders && requiredHeaders.length > 0 && (
            <Box gap="0.5rem" width="100%" direction="column">
              <Box pad={{ bottom: '0.25rem' }}>
                <Text size="1rem" weight={700} color={page.modalText}>
                  Required
                </Text>
              </Box>
              {requiredHeaders.map(({ name, description }) => (
                <Box key={name} direction="row">
                  <Text size="0.9rem" color={page.modalText} weight={600}>
                    {name}
                    {description && (
                      <>
                        :&nbsp;
                        <Text size="0.875rem" weight={400}>{description}</Text>
                      </>
                    )}
                  </Text>
                </Box>
              ))}
              <Box gap="0.5rem" width="100%" direction="column" pad={{ top: '1rem' }}>
                <Box pad={{ bottom: '0.25rem' }}>
                  <Text size="1rem" weight={700} color={page.modalText} pad={{ top: '1rem' }}>
                    Optional
                  </Text>
                </Box>
                {optionalHeaders.map(({ name, description }) => (
                  <Box key={name} direction="row">
                    <Text size="0.9rem" color={page.modalText} weight={600}>
                      {name}
                      {description && (
                        <>
                          :&nbsp;
                          <Text size="0.875rem" weight={400}>{description}</Text>
                        </>
                      )}
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box pad={{ top: '0.5rem' }}>
                <AppButton
                  overrideHover
                  onClick={() => handleDownloadExampleCSV()}
                  level="dynamicSmall"
                  width="14rem"
                  color={button.primaryText}
                  bgColor={button.primaryBg}
                  fontWeight={600}
                  label="Download CSV Template"
                />
              </Box>
            </Box>
          )}
        </SimpleModal>
      )}
    </Box>
  );
};

MediaUpload.propTypes = {
  maxInputs: PropTypes.number,
  singleFile: PropTypes.bool,
  searchTypeKey: PropTypes.string,
  handleFormValues: PropTypes.func.isRequired,
  inputConfig: PropTypes.shape({
    acceptedFileType: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  small: PropTypes.bool.isRequired,
  isBulkSearch: PropTypes.bool,
  useCatalog: PropTypes.bool,
  onUseCatalogChange: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
};

export default MediaUpload;
